import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PenLine, RotateCcw, Save } from "lucide-react";
import api, { API } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const logoSrc = (u) => (u?.startsWith("/api") ? `${API}${u.slice(4)}` : u);

const Preview = ({ text, logo, logoUrl, accent }) => {
  const [first, ...rest] = text.split("\n").filter((x) => x.trim());
  return (
    <div className="rounded-lg border border-border bg-white text-slate-700 p-4" data-testid="signature-preview" data-no-translate>
      <div className="text-sm text-slate-500 mb-3">Gentile cliente, in allegato il documento richiesto…</div>
      <div className="flex gap-4 pt-3" style={{ borderTop: `2px solid ${accent}` }}>
        {logo && logoUrl && <img src={logoSrc(logoUrl)} alt="logo" className="max-h-16 max-w-[160px] object-contain" />}
        <div className="text-[12.5px] leading-relaxed">
          {first && <div className="font-bold text-[15px]" style={{ color: accent }}>{first}</div>}
          {rest.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
};

// Firma delle email: generata dai dati della ditta (logo incluso) e modificabile.
export const SignatureEditor = () => {
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [logo, setLogo] = useState(true);
  useEffect(() => { api.get("/mail/signature").then((r) => { setS(r.data); setText(r.data.text ?? r.data.default_text); setLogo(r.data.logo); }).catch(() => {}); }, []);
  const save = async (t = text) => {
    try { await api.put("/mail/signature", { text: t, logo }); toast.success("Firma salvata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reset = () => { setText(s.default_text); api.put("/mail/signature", { text: null, logo }).then(() => toast.success("Firma automatica ripristinata")); };
  if (!s) return null;
  return (
    <div className="space-y-3 border-t border-border pt-5" data-testid="signature-editor">
      <div className="flex items-center gap-2"><PenLine className="w-4 h-4 text-primary" /><Label className="font-semibold">Firma delle email</Label></div>
      <p className="text-xs text-muted-foreground">Creata in automatico con logo e dati della ditta. Una riga per voce: la prima è in evidenza.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} className="font-sans text-sm" data-testid="signature-text-input" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={logo} onCheckedChange={setLogo} data-testid="signature-logo-switch" /> Mostra il logo</label>
        </div>
        <Preview text={text} logo={logo} logoUrl={s.logo_url} accent={s.accent} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={reset} data-testid="signature-reset-button"><RotateCcw className="w-4 h-4 mr-1.5" /> Automatica</Button>
        <Button size="sm" onClick={() => save()} data-testid="signature-save-button"><Save className="w-4 h-4 mr-1.5" /> Salva firma</Button>
      </div>
    </div>
  );
};

export default SignatureEditor;
